import { Transition } from "components/shared/column";
import { Checkbox, Field, Select } from "components/shared/form";
import Loader from "components/shared/loader";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import {
    booksEditPermissionsFlags,
    featureModuleTypes,
    organizationFlags,
    organizationFlagsEnum,
    tileTypes,
} from "helpers/enums";
import { paths } from "helpers/paths";
import { enumLabel, optionName } from "helpers/system";
import React, { useEffect, useMemo, useState } from "react";
import { CustomScroll } from "react-custom-scroll";
import { applyOrganizationInitialSettings, getOrganizationInitialSettings } from "sources/admin";
import { getInitialSettingsList } from "sources/featureModules";

const AdminOrganizationSettings = (props) => {
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({});
    const [featureModules, setFeatureModules] = useState([]);

    useEffect(() => {
        Promise.all([getOrganizationInitialSettings(), getInitialSettingsList()]).then((res) => {
            setFormData(res[0]);
            setFeatureModules(res[1]);
            setLoading(false);
        });
    }, []);

    const submitForm = (e) => {
        e.preventDefault();
        setLoading(true);
        applyOrganizationInitialSettings(formData).then(() => {
            setLoading(false);
        });
    };

    const setValue = ({ name, value }) => {
        setFormData({ ...formData, [name]: value });
    };

    const close = () => {
        const { history, back } = props;
        back ? back() : history.push(paths.adminOrganizations);
    };

    const featureOptions = useMemo(() => {
        return featureModules.reduce(
            (prev, curr) =>
                prev.concat({
                    label: enumLabel(featureModuleTypes, curr.type),
                    value: curr.id,
                    disabled: !curr.isEnabled,
                }),
            [],
        );
    }, [featureModules]);

    const enumToConfig = (enums) =>
        enums.map((item) => ({
            label: item.label,
            inputProps: { name: optionName(item.value) },
        }));
    const organizationFlagsConfig = useMemo(
        () => enumToConfig(organizationFlags.filter((item) => item.value !== organizationFlagsEnum.CanCreateBids)),
        [],
    );
    const booksEditPermissionsFlagsConfig = useMemo(() => enumToConfig(booksEditPermissionsFlags), []);

    return (
        <Transition dir="full">
            <div className="page-book__list book-list list book-list--nofilter">
                <div className="book-list__head-wrap border-bottom border-secondary">
                    <div className="book-list__head">
                        <div className="book-list__head-close close close--sm" onClick={close} />
                        <div className="book-list__head-title">Подключаемые модули при создании организации</div>
                    </div>
                </div>
                <div className="list__body-wrap">
                    {loading && (
                        <div className="loader-snippet--disable-loader">
                            <Loader />
                        </div>
                    )}
                    <div className="list__body d-flex flex-column">
                        <CustomScroll heightRelativeToParent="100%">
                            <form onSubmit={submitForm}>
                                <div className="p-4">
                                    <h5>Настройки</h5>
                                    <Select
                                        {...defaultMultipleSelectProps}
                                        components={{ ValueContainer }}
                                        label="Модули по умолчанию"
                                        onChange={setValue}
                                        inputProps={{ name: "featureModuleIds" }}
                                        value={formData.featureModuleIds || []}
                                        options={featureOptions}
                                        menuPortalTarget={document.body}
                                    />
                                    <Select
                                        label="Тайлы для карт"
                                        inputProps={{ name: "tileTypes" }}
                                        options={tileTypes}
                                        isMulti
                                        onChange={setValue}
                                        value={formData.tileTypes || []}
                                    />
                                    <Field
                                        label="Период анализа посещения точек"
                                        onChange={setValue}
                                        inputProps={{
                                            name: "bidRecalculationOffsetHours",
                                            type: "number",
                                            step: "1",
                                            placeholder: "От 48 до 96",
                                            min: 48,
                                            max: 96,
                                        }}
                                        value={formData.bidRecalculationOffsetHours || ""}
                                    />
                                    <h5 className="mt-3">Флаги</h5>
                                    {organizationFlagsConfig.map((item, i) => (
                                        <Checkbox
                                            fieldBeforeLabel
                                            key={i}
                                            labelWrapper="col-auto col-form-label col-form-label-sm pl-0"
                                            fieldWrapper="col-auto"
                                            bWrapper="row mb-2 align-items-center"
                                            onChange={setValue}
                                            value={formData[item.inputProps.name] || false}
                                            {...item}
                                        />
                                    ))}
                                    <Checkbox
                                        fieldBeforeLabel
                                        labelWrapper="col-auto col-form-label col-form-label-sm pl-0"
                                        fieldWrapper="col-auto"
                                        bWrapper="row mb-2 align-items-center"
                                        onChange={setValue}
                                        value={formData.usesDriverManualBidCompletion || false}
                                        label="Отображать завершенную заявку водителю"
                                        inputProps={{ name: "usesDriverManualBidCompletion" }}
                                    />
                                    <Checkbox
                                        fieldBeforeLabel
                                        labelWrapper="col-auto col-form-label col-form-label-sm pl-0"
                                        fieldWrapper="col-auto"
                                        bWrapper="row mb-2 align-items-center"
                                        onChange={setValue}
                                        value={formData.forceCompleteOnLastPointManualDates || false}
                                        label={enumLabel(
                                            organizationFlags,
                                            organizationFlagsEnum.ForceCompleteOnLastPointManualDates,
                                        )}
                                        inputProps={{ name: "forceCompleteOnLastPointManualDates" }}
                                    />
                                    <h5 className="mt-3">Доступ к редактированию справочников</h5>
                                    {booksEditPermissionsFlagsConfig.map((item, i) => (
                                        <Checkbox
                                            fieldBeforeLabel
                                            key={i}
                                            labelWrapper="col-auto col-form-label col-form-label-sm pl-0"
                                            fieldWrapper="col-auto"
                                            bWrapper="row mb-2 align-items-center"
                                            onChange={setValue}
                                            value={formData[item.inputProps.name] || false}
                                            {...item}
                                        />
                                    ))}
                                    <div className="d-flex justify-content-end">
                                        <button className="btn btn-sm btn-brand" type="submit">
                                            Сохранить
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </CustomScroll>
                    </div>
                </div>
            </div>
        </Transition>
    );
};

export default AdminOrganizationSettings;
